import { Alert, Button, Descriptions, Flex, Input, Modal, Radio, Spin, Switch, Typography } from "antd";
import type { InputRef } from "antd";
import { observer } from "mobx-react-lite";
import { useEffect, useRef, useState } from "react";
import paymentCheckoutStore, { PaymentMethod } from "../stores/paymentCheckoutStore";
import { createCardCryptogram, getCardFieldErrors } from "../services/cloudPaymentsCheckout";
import http from "../services/http";
import authStore from "../stores/authStore";
import styles from "./styles/PaymentCheckoutModal.module.scss";
import { FEATURE_MARKETING } from "../config/featureMarketing";

interface PaymentPricing {
  foreignCardSurchargePercent: number;
  terms: { months: number }[];
}
const recurringMethods: PaymentMethod[] = ["card"];
const methods: { value: PaymentMethod; title: string; hint: string }[] = [
  { value: "card", title: "Российская карта", hint: "Только карты банков России" },
  { value: "sbp", title: "СБП", hint: "В приложении вашего банка" },
  { value: "tinkoff_pay", title: "T‑Pay", hint: "Через приложение Т‑Банка" },
  { value: "sberpay", title: "SberPay", hint: "Подтверждение в СберБанк Онлайн" },
  { value: "foreign_card", title: "Иностранная карта", hint: "Карты иностранных банков" },
];

export default observer(function PaymentCheckoutModal() {
  // Temporarily limit alternative payment methods to the checkout testers.
  const availableMethods = methods.filter((item) =>
    ["ilyha.gor@mail.ru", "potolok-party@test.com", "stanislav.marinich@gmail.com"].includes(authStore.config?.email?.toLowerCase() || "")
      || item.value === "card" || item.value === "foreign_card"
  );
  const [term, setTerm] = useState(1);
  const [method, setMethod] = useState<PaymentMethod>("card");
  const [recurring, setRecurring] = useState(false);
  const [phone, setPhone] = useState("");
  const [card, setCard] = useState({ cardNumber: "", expDateMonth: "", expDateYear: "", cvv: "" });
  const yearInput = useRef<InputRef>(null);
  const [cardSubmitted, setCardSubmitted] = useState(false);
  const cardFieldErrors = getCardFieldErrors(card);
  const [paying, setPaying] = useState(false);
  const [cardError, setCardError] = useState("");
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const [pricing, setPricing] = useState<PaymentPricing | null>(null);
  const [pricingError, setPricingError] = useState("");
  const [pricingRetry, setPricingRetry] = useState(0);
  const checkoutOpen = paymentCheckoutStore.open;
  const payment = paymentCheckoutStore.payment;
  const summary = payment ? (payment.product || {
    kind: "feature", title: payment.description || "Покупка", description: "",
  }) : paymentCheckoutStore.options;
  const isCombo = summary?.code === "combo";
  const terms = (pricing?.terms ?? []).filter((item) => !isCombo || item.months === 12);
  const quantity = Number(payment?.quantity ?? summary?.quantity ?? 1);
  const selectedTerm = Number(payment?.termMonths ?? (isCombo ? 12 : term));
  const periodLabel = selectedTerm === 12 ? "1 год" : "1 месяц";
  const yearlyAvailable = summary?.kind !== "feature" || summary?.yearlyPrice != null;
  const periodUnitPrice = payment
    ? payment.unitPrice ?? (payment.monthlyUnitPrice == null ? null : Number(payment.monthlyUnitPrice) * selectedTerm)
    : selectedTerm === 12
      ? summary?.kind === "feature" ? summary?.yearlyPrice : Number(summary?.monthlyPrice) * 12
      : summary?.monthlyPrice;
  const periodAmount = periodUnitPrice == null ? null : Number(periodUnitPrice) * quantity;
  const surcharge = Number(payment?.surchargePercent ?? (method === "foreign_card" ? pricing?.foreignCardSurchargePercent ?? 0 : 0));
  const subtotal = Math.round((periodAmount ?? 0) * 100) / 100;
  const totalAmount = payment ? Number(payment.amount) : Math.round(subtotal * (1 + surcharge / 100) * 100) / 100;
  const formatMoney = (amount: number) => new Intl.NumberFormat("ru-RU", {
    style: "currency", currency: payment?.currency || "RUB", maximumFractionDigits: 2,
  }).format(amount);
  const marketing = summary?.kind === "feature" ? FEATURE_MARKETING[summary.code] : undefined;
  const description = summary?.description ?? "";

  useEffect(() => {
    if (!checkoutOpen) {
      setPricing(null);
      setPricingError("");
      return;
    }
    let active = true;
    setPricing(null);
    setPricingError("");
    http.get<{ data: PaymentPricing }>("/payment-pricing").then((response) => {
      if (active) setPricing(response.data.data);
    }).catch(() => {
      if (active) setPricingError("Не удалось загрузить условия оплаты. Попробуйте ещё раз.");
    });
    return () => { active = false; };
  }, [checkoutOpen, pricingRetry]);

  useEffect(() => {
    if (!document.getElementById("cloudpayments-checkout")) {
      const script = document.createElement("script");
      script.id = "cloudpayments-checkout";
      script.src = "https://checkout.cloudpayments.ru/checkout.js";
      script.async = true;
      document.head.appendChild(script);
    }
  }, []);

  useEffect(() => {
    const paymentId = new URLSearchParams(window.location.search).get("paymentId");
    if (paymentId) void paymentCheckoutStore.resume(paymentId);
  }, []);

  useEffect(() => {
    if (!checkoutOpen) {
      setCard({ cardNumber: "", expDateMonth: "", expDateYear: "", cvv: "" });
      setCardError("");
      setCardSubmitted(false);
      setTerm(1);
      setMethod("card");
      setRecurring(false);
      setPhone("");
      setDescriptionExpanded(false);
    }
  }, [checkoutOpen]);

  const paymentId = paymentCheckoutStore.payment?.id;
  const paymentStatus = paymentCheckoutStore.payment?.status;
  useEffect(() => {
    if (!checkoutOpen || !paymentId || ["completed", "failed", "canceled"].includes(paymentStatus)) return;
    let active = true;
    let timer: ReturnType<typeof setTimeout>;
    const refresh = async () => {
      try { await paymentCheckoutStore.refreshPayment(paymentId); }
      catch { /* A temporary network failure does not change payment status. */ }
      if (active) timer = setTimeout(refresh, 3000);
    };
    timer = setTimeout(refresh, 3000);
    return () => { active = false; clearTimeout(timer); };
  }, [checkoutOpen, paymentId, paymentStatus]);

  useEffect(() => {
    if (!checkoutOpen || !paymentId || paymentStatus !== "completed") return;
    void paymentCheckoutStore.refreshPurchases();
  }, [checkoutOpen, paymentId, paymentStatus]);

  const create = async () => {
    if (!pricing || periodAmount === null || !terms.some((item) => item.months === selectedTerm)) return;
    const normalizedPhone = phone.replace(/\D/g, "").replace(/^8/, "7");
    const userAgent = navigator.userAgent;
    try {
      const payment = await paymentCheckoutStore.create(selectedTerm, method, !isCombo && recurring && recurringMethods.includes(method), normalizedPhone, {
        browser: (navigator as any).userAgentData?.brands?.[0]?.brand || navigator.appName,
        os: (navigator as any).userAgentData?.platform || navigator.platform || "Unknown",
        device: /Android|iPhone|iPad|iPod/i.test(userAgent) ? "Mobile" : "DesktopWeb",
        webview: false,
      });
      const url = payment.nextAction?.Model?.QrUrl;
      if (url) window.location.assign(url);
    } catch { /* The store displays the API error. */ }
  };

  const payCard = async () => {
    if (paying) return;
    setCardError("");
    setCardSubmitted(true);
    if (Object.values(cardFieldErrors).some(Boolean)) return;
    setPaying(true);
    try {
      const cryptogram = await createCardCryptogram(card);
      const response = await http.post(`/payment-intents/${paymentCheckoutStore.payment.id}/card`, { cardCryptogramPacket: cryptogram });
      paymentCheckoutStore.setPayment(response.data.data);
      setCardSubmitted(false);
      setCard({ cardNumber: "", expDateMonth: "", expDateYear: "", cvv: "" });
      const action = response.data.data.nextAction;
      if (action?.type === "three_ds") {
        const form = document.createElement("form");
        form.method = "POST";
        form.action = action.acsUrl;
        form.style.display = "none";

        for (const [name, value] of Object.entries({
          MD: action.transactionId,
          PaReq: action.paReq,
          TermUrl: action.termUrl,
        })) {
          const input = document.createElement("input");
          input.type = "hidden";
          input.name = name;
          input.value = String(value);
          form.appendChild(input);
        }

        document.body.appendChild(form);
        form.submit();
      }
    } catch (error: any) {
      if (error?.response?.data?.data) {
        const payment = error.response.data.data;
        paymentCheckoutStore.setPayment({ ...payment, nextAction: payment.status === "failed" ? { type: "card_cryptogram" } : { type: "processing" } });
      }
      const messages = error?.response?.data?.message || error?.message || (typeof error === "object" && error ? Object.values(error).filter((value) => typeof value === "string").join(", ") : "Не удалось выполнить оплату");
      setCardError(messages || "Проверьте данные карты.");
    } finally { setPaying(false); }
  };

  const canPayCard = payment?.nextAction?.type === "card_cryptogram" && !["completed", "canceled"].includes(paymentStatus);
  const visibleCardError = paymentStatus !== "completed" ? cardError : "";
  const paymentMessages = <>
    {paymentStatus === "completed" && <Alert className={styles.status} type="success" showIcon message="Оплата прошла успешно" description="Покупка активирована." />}
    {paymentStatus === "failed" && <Alert className={styles.status} type="error" showIcon message="Оплата не прошла" description={visibleCardError || payment.failureMessage || "Оплата отклонена банком. Попробуйте другую карту."} />}
    {paymentStatus === "canceled" && <Alert className={styles.status} type="warning" showIcon message="Оплата отменена" description="Чтобы оплатить покупку, оформите новый платёж." />}
    {paymentStatus === "pending" && !canPayCard && <Alert className={styles.status} type="info" showIcon message="Ожидаем подтверждение оплаты" description={payment.method === "sberpay" ? "Подтвердите платёж в приложении банка. Статус обновится автоматически." : "Статус обновится автоматически после ответа платёжной системы."} />}
    {visibleCardError && paymentStatus !== "failed" && <Alert className={styles.status} type="error" showIcon message="Не удалось выполнить оплату" description={visibleCardError} />}
    {paymentCheckoutStore.error && <Alert className={styles.status} type="error" showIcon message="Ошибка оплаты" description={paymentCheckoutStore.error} />}
  </>;

  return <Modal open={checkoutOpen} onCancel={() => paymentCheckoutStore.close()} footer={null} title="Оформление покупки" width={600} className={styles.modal} destroyOnClose style={{ top: 24 }}>
    <div className={styles.content}>
      <div className={styles.progress} aria-label="Этап оплаты">
        <span className={!payment ? styles.activeStep : ""}>1. Условия покупки</span>
        <span className={payment && !["completed", "failed", "canceled"].includes(paymentStatus) ? styles.activeStep : ""}>2. Оплата</span>
        <span className={["completed", "failed", "canceled"].includes(paymentStatus) ? styles.activeStep : ""}>3. Результат</span>
      </div>

      {summary && <section className={styles.summary}>
        {marketing?.promotion && <Alert type="warning" message={marketing.promotion} className={styles.status} />}
        <Typography.Text className={styles.eyebrow}>{summary.kind === "tariff" ? "Тариф" : "Управление функционалом"}</Typography.Text>
        <Typography.Title level={4} className={styles.productTitle}>{summary.title}</Typography.Title>
        {description && <Typography.Paragraph
          type="secondary"
          className={styles.description}
          ellipsis={{
            rows: 3,
            expandable: "collapsible",
            expanded: descriptionExpanded,
            symbol: (expanded) => expanded ? "Свернуть описание" : "Показать полностью",
            onExpand: (_, { expanded }) => setDescriptionExpanded(expanded),
          }}
        >
          {description}
        </Typography.Paragraph>}
        <Descriptions size="small" column={1} colon={false}>
          {periodAmount !== null && <Descriptions.Item label={selectedTerm === 12 ? "Стоимость за год" : "Стоимость за месяц"}>{formatMoney(periodAmount)}</Descriptions.Item>}
          <Descriptions.Item label="Количество">{quantity}</Descriptions.Item>
          <Descriptions.Item label="Период оплаты">{periodLabel}</Descriptions.Item>
          {surcharge > 0 && <Descriptions.Item label="Доплата за иностранную карту">{surcharge}%</Descriptions.Item>}
          {payment && <Descriptions.Item label="Способ оплаты">{methods.find((item) => item.value === payment.method)?.title || payment.method}</Descriptions.Item>}
          {payment && <Descriptions.Item label="Автопродление">{payment.recurring ? selectedTerm === 12 ? "Ежегодно" : "Ежемесячно" : "Отключено"}</Descriptions.Item>}
        </Descriptions>
      </section>}

      {(payment || (summary && pricing)) && <section className={styles.total} aria-label="Итоговая стоимость" aria-live="polite">
        <span className={styles.totalLabel}>{paymentStatus === "completed" ? "Оплачено" : "Итого к оплате"}</span>
        <strong className={styles.totalAmount}>{formatMoney(totalAmount)}</strong>
        <span className={styles.totalHint}>За {periodLabel}{surcharge > 0 ? ` · доплата ${surcharge}% учтена` : ""}</span>
        {!payment && <span className={styles.estimate}>Предварительный расчёт. Точная сумма — на следующем шаге.</span>}
      </section>}

      {!payment && summary && !pricing && (pricingError
        ? <Alert type="error" showIcon message={pricingError} action={<Button onClick={() => setPricingRetry((value) => value + 1)}>Повторить</Button>} />
        : <Spin tip="Загружаем условия оплаты"><div style={{ minHeight: 64 }} /></Spin>)}
      {!payment && summary && pricing && <>
        <section className={styles.section}>
          <Typography.Title level={5}>Период оплаты</Typography.Title>
          <Radio.Group className={styles.terms} value={selectedTerm} onChange={(e) => setTerm(e.target.value)}>
            {terms.map((item) => <Radio.Button key={item.months} value={item.months} disabled={item.months === 12 && !yearlyAvailable}>
              <strong>{item.months === 12 ? "1 год" : "1 месяц"}</strong>
              {item.months === 12 && <span className={styles.methodHint}>{isCombo ? "Скидка 50%" : "Скидка ≈ 20%"}</span>}
            </Radio.Button>)}
          </Radio.Group>
        </section>
        <section className={styles.section}>
          <Typography.Title level={5}>Способ оплаты</Typography.Title>
          <Radio.Group className={styles.methods} value={method} onChange={(e) => { setMethod(e.target.value); if (!recurringMethods.includes(e.target.value)) setRecurring(false); }}>
            {availableMethods.map((item) => <Radio key={item.value} value={item.value} className={method === item.value ? styles.selectedMethod : ""}>
              <strong>{item.title}</strong><span className={styles.methodHint}>{item.value === "foreign_card"
                ? `${item.hint} (доплата ${pricing.foreignCardSurchargePercent}% на комиссию)` : item.hint}</span>
            </Radio>)}
          </Radio.Group>
          {method === "sberpay" && <label className={styles.phone}>Телефон для push/SMS от Сбера<Input size="large" value={phone} inputMode="tel" placeholder="+7 999 000-11-22" maxLength={18} onChange={(e) => setPhone(e.target.value.replace(/[^\d+() -]/g, ""))} /></label>}
          {!isCombo && <div className={styles.recurring}>
            <Switch aria-label="Автопродление" checked={recurring} disabled={!recurringMethods.includes(method)} onChange={setRecurring} />
            <div><Typography.Text strong disabled={!recurringMethods.includes(method)}>{term === 12 ? "Ежегодное автопродление" : "Ежемесячное автопродление"}</Typography.Text><span className={styles.methodHint}>{recurringMethods.includes(method) ? "Продлевайте доступ автоматически" : "Доступно при оплате банковской картой"}</span></div>
          </div>}
        </section>
      </>}

      {canPayCard && <section className={styles.cardSection}>
        <Typography.Title level={5}>{paymentStatus === "failed" ? "Попробуйте оплатить ещё раз" : "Данные банковской карты"}</Typography.Title>
        <Typography.Paragraph type="secondary">{payment.method === "foreign_card" ? "Используйте карту, выпущенную банком за пределами России." : "Используйте карту, выпущенную российским банком."}</Typography.Paragraph>
        <Flex vertical gap={20}>
          <label className={styles.field}>Номер карты<Input size="large" autoComplete="cc-number" status={cardSubmitted && cardFieldErrors.cardNumber ? "error" : undefined} aria-invalid={cardSubmitted && cardFieldErrors.cardNumber} placeholder="0000 0000 0000 0000" maxLength={23} inputMode="numeric" value={card.cardNumber} onChange={(e) => { const digits = e.target.value.replace(/\D/g, "").slice(0, 19); setCard({ ...card, cardNumber: digits.replace(/(.{4})/g, "$1 ").trim() }); }} /></label>
          <Flex gap={20}>
            <div className={styles.field}><span>Срок действия</span><Flex align="center" className={styles.expiry}>
              <Input size="large" aria-label="Месяц" autoComplete="cc-exp-month" status={cardSubmitted && cardFieldErrors.expDateMonth ? "error" : undefined} aria-invalid={cardSubmitted && cardFieldErrors.expDateMonth} placeholder="ММ" bordered={false} maxLength={2} inputMode="numeric" value={card.expDateMonth}
                onChange={(e) => {
                  const month = e.target.value.replace(/\D/g, "");
                  if (/^(?:0?[1-9]|1[0-2]|0)?$/.test(month)) {
                    setCard((current) => ({ ...current, expDateMonth: month }));
                    if (month.length === 2) yearInput.current?.focus();
                  }
                }}
                onBlur={() => {
                  setCard((current) => ({ ...current, expDateMonth: current.expDateMonth && current.expDateMonth !== "0" ? current.expDateMonth.padStart(2, "0") : "" }));
                }}
              />
              <span>/</span><Input ref={yearInput} size="large" aria-label="Год" autoComplete="cc-exp-year" status={cardSubmitted && cardFieldErrors.expDateYear ? "error" : undefined} aria-invalid={cardSubmitted && cardFieldErrors.expDateYear} placeholder="ГГ" bordered={false} maxLength={2} inputMode="numeric" value={card.expDateYear} onChange={(e) => setCard({ ...card, expDateYear: e.target.value.replace(/\D/g, "") })} />
            </Flex></div>
            <label className={styles.field}>CVV / CVC<Input size="large" type="password" autoComplete="off" status={cardSubmitted && cardFieldErrors.cvv ? "error" : undefined} aria-invalid={cardSubmitted && cardFieldErrors.cvv} placeholder="•••" maxLength={3} inputMode="numeric" value={card.cvv} onChange={(e) => setCard({ ...card, cvv: e.target.value.replace(/\D/g, "").slice(0, 3) })} /></label>
          </Flex>
          {paymentMessages}
          <Button type="primary" size="large" block loading={paying} onClick={payCard} className={styles.payButton}>Оплатить {formatMoney(totalAmount)}</Button>
        </Flex>
      </section>}
      {!canPayCard && paymentMessages}
      {paymentStatus === "completed" && <Button type="primary" size="large" block onClick={() => paymentCheckoutStore.close()}>Готово</Button>}
      {summary && !payment && <Button type="primary" size="large" block disabled={!pricing || periodAmount === null} loading={paymentCheckoutStore.loading} onClick={create}>Перейти к оплате</Button>}
    </div>
  </Modal>;
});
